import { Check, QrCode, Save, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { FitChoice, FitPreference } from "../../shared/types";
import { FitPhoto } from "../components/FitPhoto";
import { FitControls } from "../components/FitControls";
import { fitOptions } from "../lib/fitExperience";
import { api } from "../lib/api";
import type { ConsumerBootstrap } from "../lib/models";

const categories: Array<{ value: FitPreference["category"]; label: string }> = [
  { value: "tshirt", label: "T-shirt" },
  { value: "shirt", label: "Camicia" },
  { value: "overshirt", label: "Overshirt" },
  { value: "knitwear", label: "Maglieria" },
];

const fits = fitOptions;

export function StylePage({ data, onComplete }: { data: ConsumerBootstrap; onComplete: () => Promise<void> | void }) {
  const firstPreference = data.preferences[0];
  const [category, setCategory] = useState<FitPreference["category"]>(firstPreference?.category ?? "tshirt");
  const stored = useMemo(() => data.preferences.find((item) => item.category === category), [category, data.preferences]);
  const [choices, setChoices] = useState<Partial<Record<FitPreference["category"], FitChoice>>>({});
  const fit = choices[category] ?? stored?.fit ?? "regular";
  const selected = fits.find((item) => item.value === fit) ?? fits[1];
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const save = async () => {
    setStatus("saving");
    try {
      await api("/preferences", { method: "PUT", body: JSON.stringify({ category, fit, chest: stored?.chest ?? 0, sleeves: stored?.sleeves ?? 0, length: stored?.length ?? 0, neck: stored?.neck ?? 0 }) });
      await onComplete();
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  };

  return <main className="consumer-main style-page">
    <header className="style-heading"><SlidersHorizontal /><div><h1>Il tuo modo di vestire.</h1><p>Scegli il volume che preferisci, per ogni tipo di capo.</p></div></header>

    <div className="style-category" role="radiogroup" aria-label="Tipo di capo">
      {categories.map((item) => <button key={item.value} type="button" role="radio" aria-checked={category === item.value} className={category === item.value ? "active" : ""} disabled={status === "saving"} onClick={() => { setCategory(item.value); setStatus("idle"); }}>{item.label}</button>)}
    </div>

    <div className="style-fit-layout"><FitPhoto fit={fit}/><div><fieldset className="fit-choice-native"><legend>Scegli la vestibilità</legend><div>{fits.map(item=><label key={item.value} className={fit === item.value ? "selected" : ""}><input type="radio" name="style-fit" checked={fit === item.value} disabled={status === "saving"} onChange={()=>{setChoices(current=>({...current,[category]:item.value}));setStatus("idle");}}/><span>{item.label}</span></label>)}</div></fieldset><p className="fit-description">{selected.description}</p><p>Le immagini illustrano il volume su una T-shirt. Il brand adatta il fit al capo scelto; le misure del corpo restano le stesse.</p><Link className="text-link" to="/demo/tshirt">Regola anche i dettagli su un capo →</Link></div></div>
    <footer className="style-actions">
      {status === "error" ? <p className="form-error" role="alert">Your style could not be saved. Try again.</p> : null}
      <button className="style-save" type="button" onClick={() => void save()} disabled={status === "saving"}>{status === "saved" ? <Check /> : <Save />}{status === "saving" ? "Salvataggio…" : status === "saved" ? "Stile salvato" : "Salva il mio stile"}</button>
      <Link to={data.bodyProfile ? "/app/pass" : "/app/scan"}><QrCode />{data.bodyProfile ? "Mostra il mio QR" : "Crea il mio profilo"}</Link>
    </footer>
  </main>;
}
